import { Box } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportLineMode, ImportStep } from 'bom/enums';
import { useGetBomImportPartColumns, useGetBomImportParts } from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import { RadioGroupInput } from 'core/components';
import { Loader } from 'core/elements';
import { useStepsContext } from 'core/states';

import { PreviewImportTable } from './PreviewImportTable';

export const PreviewImport = () => {
  const { t } = useTranslation();

  const {
    partImportId,
    type,
    isQuoteImport,
    setUpdateMatchingLines,
    previewCombineDuplicates,
    shouldUpdateMatchingLines,
  } = useBomPartsImportContext();
  const { activeStep } = useStepsContext();

  const { columns, isLoading: isPartColumnsLoading } = useGetBomImportPartColumns(
    {
      partImport: partImportId ?? 0,
    },
    ImportStep.PREVIEW === activeStep.type
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetBomImportParts({ partImport: partImportId ?? 0 }, columns?.length > 0);

  const isUpdateMatchingAllowed = true === previewCombineDuplicates;

  const importLineModeChoices = useMemo(
    () => [
      {
        value: ImportLineMode.UPDATE_MATCHING,
        label: t('labelUpdateMatchingQuoteLines'),
        disabled: !isUpdateMatchingAllowed,
        ...(!isUpdateMatchingAllowed && {
          tooltip: t('tooltipUpdateMatchingLinesRequiresCombine'),
        }),
      },
      {
        value: ImportLineMode.ADD_ALL,
        label: t('labelAddAllAsNewLines'),
      },
    ],
    [t, isUpdateMatchingAllowed]
  );

  if (isPartColumnsLoading) {
    return <Loader />;
  }

  return (
    <>
      {isQuoteImport && (
        <Box sx={{ marginBottom: 1 }}>
          <RadioGroupInput
            name="importLineMode"
            value={
              shouldUpdateMatchingLines ? ImportLineMode.UPDATE_MATCHING : ImportLineMode.ADD_ALL
            }
            choices={importLineModeChoices}
            onChange={(event) =>
              setUpdateMatchingLines(
                ImportLineMode.UPDATE_MATCHING === (event.target as HTMLInputElement).value
              )
            }
          />
        </Box>
      )}

      <PreviewImportTable
        type={type}
        columns={columns}
        records={records}
        isDataLoading={isDataLoading}
        loadingPercentage={loadingPercentage}
        dataCurrentPage={currentPage}
        showQuoteLineMatchNotes={shouldUpdateMatchingLines}
      />
    </>
  );
};
